import { motion, AnimatePresence } from 'framer-motion'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'

/**
 * ADR-082 boundary overlay — a session belongs to the day it began in. Two
 * situations share this one element, distinguished only by body copy:
 *
 * - 'interrupted': the person was mid-study when the boundary passed. The
 *   card behind this overlay is the one they were just looking at, left
 *   exactly as it was — frozen, not replaced — so it reads as "this belongs
 *   to the day that ended" rather than as an empty state.
 * - 'returned': they arrived to find the session already closed; the card
 *   behind this overlay is the first card of the new session that replaced
 *   it, already loaded and ready the moment the overlay is dismissed.
 *
 * Matches ExplainModal's dialog pattern (the existing modal on this surface:
 * `--z-modal-backdrop` / `--z-modal`, `--shadow-float`, the same panel radius
 * and typography) with one deliberate difference: no Escape handler and no
 * backdrop onClick. Cleared only by its own button — the boundary is not a
 * dismissible notice, it is why the card underneath cannot be answered.
 *
 * Covers everything below LearnPage's header — never the header itself, so
 * the Back button stays reachable and visible the entire time this is open,
 * and this overlay's button is never the only way off the screen.
 *
 * Positioned via plain `position: absolute` (`top: topOffset`) against the
 * grid container, not via `gridRow` placement. A `gridRow`-spanning grid item
 * was tried first: an EXPLICITLY-placed grid item reserves its cells before
 * the header/card/input rows are auto-placed, so it displaced that real,
 * auto-placed content into brand-new implicit rows sized to their own
 * (tiny) content instead of the intended `auto 1fr auto` tracks — verified
 * on device, the card and input squeezed to a few dozen pixels each. An
 * absolutely-positioned element with no `gridRow`/`gridColumn` set takes no
 * part in grid placement at all, so this sidesteps that failure mode
 * entirely; `topOffset` (the header's live measured height, from a
 * ResizeObserver in LearnPage) is the only coordinate this needs from its
 * caller. The grid container is already a valid containing block via its own
 * framer-motion `transform`.
 */
export default function DayBoundaryOverlay({
  reason,
  onContinue,
  busy = false,
  topOffset,
}: {
  reason: 'interrupted' | 'returned'
  onContinue: () => void
  busy?: boolean
  /** Pixel height of LearnPage's header row — the overlay starts just below it. */
  topOffset: number
}) {
  const { tr } = useLocale()
  const message = reason === 'interrupted' ? tr.learn.dayBoundaryInterrupted : tr.learn.dayBoundaryReturned
  // ADR-106 P3: mounted only while shown.
  useSwipeNavBlock(true, 'day-boundary-overlay')

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        // No onClick here — the scrim is not a dismiss target (see module doc).
        style={{
          position: 'absolute',
          top: topOffset,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
          zIndex: 'var(--z-modal-backdrop)' as string,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 16px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-label={message}
          style={{
            width: 'min(420px, 100%)',
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 24,
            boxShadow: 'var(--shadow-float)',
            padding: '28px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 18,
            textAlign: 'center',
            zIndex: 'var(--z-modal)' as string,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.55,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-ui)',
            }}
          >
            {message}
          </p>
          <button
            onClick={onContinue}
            disabled={busy}
            style={{
              minHeight: 44,
              padding: '10px 28px',
              borderRadius: 50,
              border: 'none',
              background: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              fontSize: 14,
              fontWeight: 700,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.7 : 1,
              fontFamily: 'var(--font-ui)',
            }}
          >
            {tr.learn.continueBtn}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
